import React, { useCallback } from 'react';
import { translations } from 'app/locales/i18n';
import { useTranslation } from 'react-i18next';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';
import { useStyles } from './DatePicker.styles';
import { useScreen } from 'app/shared/hooks';
import { CustomDateInput } from './CustomDateInput';

interface ICustomDatePickerProps {
  startDate?: Date | null;
  endDate?: Date | null;
  withEndDate?: boolean;
  isStartDateRequired?: boolean;
  minDate?: Date | null;
  changeDateHandler: (startDate?: Date | null, endDate?: Date | null) => void;
  isUpcoming?: boolean;
}

const mobileView = 600;

const validDateOrUndefined = (date?: Date | null) =>
  date instanceof Date && !Number.isNaN(date.getTime()) ? date : undefined;

const CustomDatePicker = ({
  startDate,
  endDate,
  withEndDate = true,
  isStartDateRequired,
  minDate,
  changeDateHandler,
  isUpcoming = false,
}: ICustomDatePickerProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { isMobile } = useScreen(mobileView);
  const validStartDate = validDateOrUndefined(startDate);
  const validEndDate = validDateOrUndefined(endDate);

  const onChange = useCallback(
    (dates: any) => {
      if (withEndDate) {
        const [start, end] = dates;
        changeDateHandler(start, end);
      } else {
        changeDateHandler(dates);
      }
    },
    [changeDateHandler, withEndDate],
  );

  const resetDateRange = useCallback(() => {
    changeDateHandler(undefined, undefined);
  }, [changeDateHandler]);

  const resetEndDate = useCallback(() => {
    changeDateHandler(startDate, undefined);
  }, [changeDateHandler, startDate]);

  const pickerProps = {
    selected: validStartDate,
    dateFormat: 'MMM dd, yyyy',
    showPopperArrow: false,
    onChange,
    placeholderText: t(translations.datePicker.mask),
    startDate: validStartDate,
    endDate: validEndDate,
    maxDate: isUpcoming ? undefined : new Date(),
    minDate: validDateOrUndefined(minDate),
    popperClassName: classes.wrapper,
    customInput: (
      <CustomDateInput
        isMobile={isMobile}
        resetDateRange={resetDateRange}
        resetEndDate={resetEndDate}
        onChange={onChange}
        placeholder={''}
        startDate={validStartDate}
        endDate={validEndDate}
        withEndDate={withEndDate}
        isStartDateRequired={isStartDateRequired}
      />
    ),
  };

  // react-datepicker 7+ types `selectsRange` as a literal `true` discriminant.
  return (
    <div className={classes.mainContainer}>
      {withEndDate ? (
        <DatePicker {...pickerProps} selectsRange />
      ) : (
        <DatePicker {...pickerProps} />
      )}
    </div>
  );
};

export { CustomDatePicker as DatePicker };
